import { type ReactNode, useEffect, useRef } from "react";
import { Icon } from "@/components/Icon";
import { useDialogs } from "@/components/dialogs";

/**
 * A bottom sheet on a phone and a centred dialog on a wider screen. Built on <dialog> so focus
 * containment, Escape and the backdrop come from the browser rather than from code here.
 *
 * With `confirmDiscard`, closing it with the X, the backdrop or Escape after something was typed
 * or picked in it asks first, so a form isn't lost by accident. Closing because the form was saved
 * (the parent setting `open` to false) never asks.
 */
export function Sheet({
  open,
  title,
  onClose,
  confirmDiscard = false,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  confirmDiscard?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const dialogs = useDialogs();
  const isOpen = useRef(open);
  const changed = useRef(false);
  const asking = useRef(false);
  const latest = useRef({ onClose, confirmDiscard, dialogs });

  useEffect(() => {
    latest.current = { onClose, confirmDiscard, dialogs };
  }, [onClose, confirmDiscard, dialogs]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    isOpen.current = open;
    if (open && !dialog.open) {
      changed.current = false;
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  /** The person closing it: asks first when there are changes to lose. */
  function requestClose() {
    const { onClose, confirmDiscard, dialogs } = latest.current;
    if (!confirmDiscard || !changed.current) {
      onClose();
      return;
    }
    if (asking.current) return;
    asking.current = true;
    void dialogs
      .confirm({
        title: "Discard your changes?",
        message: "What you changed here isn't saved yet.",
        confirmLabel: "Discard",
        cancelLabel: "Keep editing",
        danger: true,
      })
      .then((discard) => {
        asking.current = false;
        if (discard) onClose();
      });
  }

  // Listened to natively: React would also pass these on to a sheet this one was opened from.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const markChanged = (event: Event) => {
      // Picking a file outside a form uploads it straight away; there's nothing to lose.
      const target = event.target;
      if (target instanceof HTMLInputElement && target.type === "file" && !target.form) return;
      changed.current = true;
    };
    const onCancel = (event: Event) => {
      // Escape: decided here rather than closing straight away.
      event.preventDefault();
      requestClose();
    };
    const onNativeClose = () => {
      if (!isOpen.current) {
        latest.current.onClose();
        return;
      }
      // The browser closed it anyway (a second Escape can't be held back): keep it open if there
      // are changes, and ask.
      if (latest.current.confirmDiscard && changed.current) dialog.showModal();
      requestClose();
    };
    dialog.addEventListener("input", markChanged);
    dialog.addEventListener("change", markChanged);
    dialog.addEventListener("cancel", onCancel);
    dialog.addEventListener("close", onNativeClose);
    return () => {
      dialog.removeEventListener("input", markChanged);
      dialog.removeEventListener("change", markChanged);
      dialog.removeEventListener("cancel", onCancel);
      dialog.removeEventListener("close", onNativeClose);
    };
    // requestClose reads everything it needs from refs.
  }, []);

  return (
    <dialog
      ref={ref}
      className="sheet"
      aria-label={title}
      onClick={(event) => {
        // A click that lands on the dialog element itself, not its content, is the backdrop.
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div className="sheet-body">
        <div className="sheet-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={requestClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        {open && children}
      </div>
    </dialog>
  );
}
